import { useCallback, useEffect, useState } from 'react';
import { AutoResponseSuggestionStatus } from 'shared/types/graphql';
import * as utils from '../utils/textComparison';
import useQuickReplyUsageTrackingServices from './useQuickReplyUsageTrackingServices';
import { distance as levenshteinDistance } from 'fastest-levenshtein';

// Define thresholds for decision-making
// Threshold for considering the suggestion significantly different based on character edits
const DISTANCE_THRESHOLD = 10;
// Threshold for considering the suggestion similar based on word overlap percentage
const SIMILARITY_THRESHOLD = 40;

type SuggestionData = {
  id?: string;
  text: string;
  status: AutoResponseSuggestionStatus;
  inUse: boolean;
};

type SuggestionMap = Record<string, SuggestionData>;

type UseQuickReplyUsageTrackingReturnValue = {
  discardSuggestion: (text: string) => void;
  setSelectedSuggestion: (text: string) => void;
  addSuggestion: (text: string) => Promise<void>;
  handleMessageSent: (channelMessageId: string, finalMessageText: string) => Promise<void>;
};

const useQuickReplyUsageTracking = (channelId?: string): UseQuickReplyUsageTrackingReturnValue => {
  const { createAutoResponseMessage, updateAutoResponseMessage } = useQuickReplyUsageTrackingServices();
  const [generatedSuggestionsMap, setGeneratedSuggestionMap] = useState<SuggestionMap>({});

  useEffect(() => {
    // Reset our suggestions if the user decided to switch conversations
    setGeneratedSuggestionMap({});
  }, [channelId]);

  const addOrUpdateSuggestionToMap = useCallback(
    (key: string, suggestion: SuggestionData) => {
      setGeneratedSuggestionMap((currentSuggestions) => ({ ...currentSuggestions, [key]: suggestion }));
    },
    [setGeneratedSuggestionMap],
  );

  // Creates a record for the generated response
  // Holds a reference locally to determine wether it was used or not
  const addSuggestion = async (text: string): Promise<void> => {
    const id = await createAutoResponseMessage({ text });
    if (id) {
      addOrUpdateSuggestionToMap(text, {
        id,
        status: AutoResponseSuggestionStatus.Generated,
        text,
        inUse: false,
      });
    }
  };

  // The user completely discarded the suggestion
  // by closing the window or generating a new one
  const discardSuggestion = (text: string): void => {
    const suggestion = generatedSuggestionsMap[text];
    if (suggestion?.id) {
      updateAutoResponseMessage({
        suggestionId: suggestion.id,
        suggestionStatus: AutoResponseSuggestionStatus.Discarded,
      });
    }
  };

  // The user decided to select the selection and it went to the message input
  const setSelectedSuggestion = (text: string): void => {
    const updatedMap = Object.entries(generatedSuggestionsMap).reduce((res, [key, value]) => {
      return {
        ...res,
        [key]: {
          ...value,
          inUse: text === key,
        },
      };
    }, {} as SuggestionMap);
    setGeneratedSuggestionMap(updatedMap);
  };

  // Helper to determine all the statuses of the generated suggestions
  const getSuggestionStatus = (suggestion: SuggestionData, finalMessageText: string): AutoResponseSuggestionStatus => {
    if (suggestion.inUse) {
      if (suggestion.text === finalMessageText) {
        return AutoResponseSuggestionStatus.Used;
      }

      // Evaluate the suggestion against the thresholds
      // If the character edit distance is below the DISTANCE_THRESHOLD or if the word overlap similarity
      // is above the SIMILARITY_THRESHOLD, classify the suggestion as 'Modified'
      const distance = levenshteinDistance(finalMessageText, suggestion.text);
      const similarity = utils.getBasicTextSimilarity(finalMessageText, suggestion.text);

      if (distance < DISTANCE_THRESHOLD || similarity > SIMILARITY_THRESHOLD) {
        return AutoResponseSuggestionStatus.Edited;
      }
    }

    return AutoResponseSuggestionStatus.Discarded;
  };

  // Once a message is sent, we want to create a relation
  // to all of the generated responses and save their final status
  // then we will clean up the suggestion map
  const handleMessageSent = async (channelMessageId: string, finalMessageText: string): Promise<void> => {
    const suggestions = Object.values(generatedSuggestionsMap);

    if (!suggestions.length) {
      return;
    }

    const promises = [];

    for (const suggestion of suggestions) {
      if (suggestion.id) {
        promises.push(
          updateAutoResponseMessage({
            suggestionId: suggestion.id,
            suggestionStatus: getSuggestionStatus(suggestion, finalMessageText),
            channelMessageId,
          }),
        );
      }
    }

    await Promise.all(promises);
    setGeneratedSuggestionMap({});
  };

  return {
    addSuggestion,
    discardSuggestion,
    setSelectedSuggestion,
    handleMessageSent,
  };
};

export default useQuickReplyUsageTracking;
